import React from "react";
import {
  Map,
  TileLayer,
  ZoomControl,
  Tooltip,
  AttributionControl,
} from "react-leaflet";
import { LatLngTuple } from "leaflet";
import CurvePath from "../utils/leaflet/CurvePath";
import HeaderNavBar from "../components/HeaderNavBar";
import { ComputeArrowheadPoints } from "../utils/leaflet/arrow-heads";
import { getClassFromConfidValue } from "../utils/confidence-colors";
import * as MapAPI from "../api/map-api";
import * as Constants from "../utils/constants";
import { TrainMode } from "../api/api-data-model";
import FilterButton from "../components/MapFilterButton";
import { WithTranslationLangProps } from "../utils/i18next/with-translation-keys";
import { withTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import MapExplainModal from "../components/MapExplainModal";

type TrainSectionPerMode = {
  modeId: string;
  sections: MapAPI.MapTrainSection[];
  ungrouped: boolean;
};

type DelayKey = "f5min" | "f10min" | "f15min" | "f20min" | "f25min" | "f30min" | "f60min" | "f120min";

interface IMapPageProps extends WithTranslationLangProps {}

interface IMapPageState {
  availableModes: TrainMode[];
  sectionsPerMode: {
    [modeKey: string]: TrainSectionPerMode;
  };
  selectedMode?: string;
  selectedDelay: DelayKey;
  years?: string[];
  selectedYear?: { label: string; value: number };
  menuVisibleToggle: boolean;
  shouldGroupLines: boolean;
  hasLoadingError: boolean;
  isPopUpDisplayed: boolean;
  shouldColorChanged: boolean;
}

class MapPage extends React.Component<IMapPageProps, IMapPageState> {
  constructor(props: IMapPageProps) {
    super(props);
    this.state = {
      availableModes: [],
      sectionsPerMode: {},
      selectedDelay: "f5min",
      shouldGroupLines: false,
      menuVisibleToggle: true,
      hasLoadingError: false,
      isPopUpDisplayed: true,
      shouldColorChanged: false,
    };
    this.handleModeSelectionChanged = this.handleModeSelectionChanged.bind(this);
    this.handleShouldGroupChanged = this.handleShouldGroupChanged.bind(this);
    this.toggleMenuVisibility = this.toggleMenuVisibility.bind(this);
    this.handleDelayChanged = this.handleDelayChanged.bind(this);
    this.handleYearChanged = this.handleYearChanged.bind(this);
    this.loadModeSection = this.loadModeSection.bind(this);
    this.getCurveWithArrow = this.getCurveWithArrow.bind(this);
    this.displayPopUp = this.displayPopUp.bind(this);
    this.closePopUp = this.closePopUp.bind(this);
    this.handleShouldColorChanged = this.handleShouldColorChanged.bind(this);
  }

  componentDidMount() {
    MapAPI.getTrainModeFilters()
      .then((result) => {
        let selectedMode = undefined;
        for (let mode of result) {
          if (mode.id.toLowerCase() === "ter") {
            selectedMode = mode.id;
            break;
          }
        }
        this.setState({
          availableModes: result,
          hasLoadingError: false,
          selectedMode: selectedMode,
        });
        if (selectedMode) {
          this.loadModeSection(selectedMode, this.state.shouldGroupLines);
        }
      })
      .catch((_) => {
        this.setState({
          hasLoadingError: true,
        });
      });
  }

  private toggleMenuVisibility(_: React.MouseEvent) {
    this.setState({ menuVisibleToggle: !this.state.menuVisibleToggle });
  }

  private handleModeSelectionChanged(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    let mode = event.target.value;
    if (
      mode &&
      (!this.state.sectionsPerMode[mode] ||
        this.state.sectionsPerMode[mode].ungrouped !==
          this.state.shouldGroupLines)
    ) {
      let modesData = { ...this.state.sectionsPerMode };
      delete modesData[mode];
      this.loadModeSection(mode, this.state.shouldGroupLines);
      this.setState({ sectionsPerMode: modesData, selectedMode: mode });
    } else {
      this.setState({ selectedMode: mode });
    }

    if (["ter", "transilien et rer"].includes(mode.toLowerCase())) {
      this.setState({ selectedDelay: "f5min" });
    }
    else {
      this.setState({ selectedDelay: "f15min" });
    }
  }

  private handleShouldGroupChanged(event: React.ChangeEvent<HTMLInputElement>) {
    let isChecked = event.target.checked;
    if (
      this.state.selectedMode &&
      (!this.state.sectionsPerMode[this.state.selectedMode] ||
        this.state.sectionsPerMode[this.state.selectedMode].ungrouped !==
          event.target.checked)
    ) {
      let modesData = { ...this.state.sectionsPerMode };
      delete modesData[this.state.selectedMode];
      this.loadModeSection(this.state.selectedMode, isChecked);
      this.setState({
        sectionsPerMode: modesData,
        shouldGroupLines: isChecked,
      });
    } else
      this.setState({
        shouldGroupLines: isChecked,
      });
  }

  private handleDelayChanged(event: React.ChangeEvent<HTMLSelectElement>) {
    let selectedValue = event.target.value as DelayKey;
    this.setState({ selectedDelay: selectedValue });
  }

  private handleYearChanged(event: React.ChangeEvent<HTMLSelectElement>) {
    let selectedValue = {
      label: event.target.options[event.target.selectedIndex].text,
      value: event.target.selectedIndex
    };
    this.setState({ selectedYear: selectedValue });
  }

  private getCurveWithArrow(pt1: LatLngTuple, pt2: LatLngTuple) {
    let offsetX = pt2[1] - pt1[1];
    let offsetY = pt2[0] - pt1[0];
    let r = Math.sqrt(Math.pow(offsetX, 2) + Math.pow(offsetY, 2));
    let theta = Math.atan2(offsetY, offsetX);
    //let thetaOffset = (0.0414 / 10) * (1 / (r === 0 ? 0.0001 : r));
    var thetaOffset = 3.14 / 10;
    let r2 = r / 2 / Math.cos(thetaOffset);
    let theta2 = theta + thetaOffset;
    let midpointX = r2 * Math.cos(theta2) + pt1[1];
    let midpointY = r2 * Math.sin(theta2) + pt1[0];
    let midpointLatLng = [midpointY, midpointX];

    var arrowPoints = ComputeArrowheadPoints(
      midpointY,
      midpointX,
      pt2[0],
      pt2[1]
    );
    let latlngs = [
      "M",
      pt1,
      "Q",
      midpointLatLng,
      pt2,
      "L",
      arrowPoints[0],
      "L",
      arrowPoints[1],
      "L",
      arrowPoints[2],
    ];
    return latlngs;
  }

  private loadModeSection(modeId: string, ungrouped: boolean) {
    MapAPI.getTrainSectionsByMode(modeId, ungrouped)
      .then((result) => {
        let modesData = { ...this.state.sectionsPerMode };
        modesData[modeId] = {
          modeId: modeId,
          sections: result,
          ungrouped: ungrouped,
        };
        let yearsTemp = result.map((value, index) => {
            return { label: Object.keys(value)[0], value: index };
        });
        let years = yearsTemp.flatMap(value => value.label);
        this.setState({
          sectionsPerMode: modesData,
          hasLoadingError: false,
          years: years,
          selectedYear: yearsTemp[0],
        });
      })
      .catch((_) => {
        this.setState({
          hasLoadingError: true,
        });
      });
  }

  private displayPopUp() {
    this.setState({ isPopUpDisplayed: true });
  }

  private closePopUp() {
    this.setState({ isPopUpDisplayed: false });
  }

  private handleShouldColorChanged(event: React.ChangeEvent<HTMLInputElement>) {
    let isChecked = event.target.checked;
    this.setState({ shouldColorChanged: isChecked });
  }

  render() {
    let showLoading = false;
    if (
      this.state.selectedMode &&
      !this.state.sectionsPerMode[this.state.selectedMode] &&
      !this.state.hasLoadingError
    ) {
      showLoading = true;
    }

    const position: LatLngTuple = [47, 2.63];
    const mapTilerKey = process.env.REACT_APP_MAP_TILER_KEY;

    return (
      <>
        <HeaderNavBar />
        <div className="map-page-body">
          <FilterButton
            className={
              (this.state.menuVisibleToggle ? "d-block " : "d-none ") +
              (this.state.menuVisibleToggle ? "d-sm-none" : "d-sm-block")
            }
            onClick={this.toggleMenuVisibility}
          />
          <div
            className={
              "map-menu " +
              (this.state.menuVisibleToggle ? "d-none " : "d-block ") +
              (this.state.menuVisibleToggle ? "d-sm-block" : "d-sm-none")
            }
          >
            <button
              className="prveo-btn menu-close-btn"
              onClick={this.toggleMenuVisibility}
            >
              &#x2715;
            </button>
            <h5>{this.props.t("type_of_service")}</h5>
            <div className="mx-2">
              {this.state.availableModes.map((mode) => (
                <label className="prveo-radio-btn" key={mode.id}>
                  {mode.name}
                  <input
                    type="radio"
                    name="map-radio-mode"
                    value={mode.id}
                    checked={this.state.selectedMode === mode.id}
                    id={"radio-mode-" + mode.id}
                    onChange={this.handleModeSelectionChanged}
                  />
                  <span className="checkmark"></span>
                </label>
              ))}
            </div>
            <h5 className="mt-4">
              {this.props.t("display")}{"\t\t"}
              <button
                className="circle-btn"
                onClick={this.displayPopUp}
              >
                i
              </button>
            </h5>
            <div className="mx-2">
              <label className="prveo-toggle-btn">
                {this.props.t("color_blind_friendly")}
                <input
                  type="checkbox"
                  name="color-blind-friendly"
                  checked={this.state.shouldColorChanged}
                  onChange={this.handleShouldColorChanged}
                />
                <span className="checkmark"></span>
              </label>
              <label className="prveo-toggle-btn">
                {this.props.t("ungroup_train_lines")}
                <input
                  type="checkbox"
                  name="map-should-group"
                  checked={this.state.shouldGroupLines}
                  onChange={this.handleShouldGroupChanged}
                />
                <span className="checkmark"></span>
              </label>
              <h6 className="mt-2">{this.props.t("type_of_delay")}</h6>
              <select
                className="custom-select custom-select-sm"
                onChange={this.handleDelayChanged}
                value={this.state.selectedDelay}
              >
                <option value="f5min">5min</option>
                <option value="f10min">10min</option>
                <option value="f15min">15min</option>
                <option value="f20min">20min</option>
                <option value="f25min">25min</option>
                <option value="f30min">30min</option>
                <option value="f60min">60min</option>
                <option value="f120min">120min</option>
              </select>
              <i style={{ fontSize: "0.8em", whiteSpace: "pre-line", display: 'block'}}> 
                <Trans> {this.props.t("map_explain_threshold_1")} </Trans>
              </i>
            </div>
            <div className="mx-2">
              <h6 className="mt-2">{this.props.t("year")}</h6>
              <select
                className="custom-select custom-select-sm"
                onChange={this.handleYearChanged}
                id="select-year"
              ></select>
              {this.state.years &&
                (!document.getElementById("select-year")?.textContent) &&
                (function (years: any) {
                  var elm = document.getElementById("select-year"),
                      df = document.createDocumentFragment();
                  for (var i = 0; i < years.length; i++) {
                    var option = document.createElement("option");
                    option.value = i.toString();
                    option.appendChild(document.createTextNode(years[i]));
                    df.appendChild(option);
                  }
                  elm?.appendChild(df);
                }(this.state.years))
              }
            </div>
          </div>

          <div className="flex-fill position-relative">
            {showLoading && (
              <div className="map-modal">
                <div className="mx-2">{this.props.t("loading")}</div>
                <div className="progress my-1">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ width: "100%" }}
                  ></div>
                </div>
              </div>
            )}
            {this.state.hasLoadingError && (
              <div className="map-modal error">
                <div className="mx-2">
                  {this.props.t("an_error_occurs")}
                  <br />
                  {this.props.t("cant_load_data")}
                </div>
              </div>
            )}
            {this.state.isPopUpDisplayed && (
              <MapExplainModal onClose={this.closePopUp} />
            )}
            {!this.state.shouldColorChanged && (
              <div className="map-legend">
                <div>{this.props.t("reliability")}</div>
                <div className="good-confid back">&gt; 95%</div>
                <div className="med-high-confid back my-1">90% - 95%</div>
                <div className="med-confid back my-1">85% - 90%</div>
                <div className="med-low-confid back my-1">80% - 85%</div>
                <div className="bad-confid back">&le; 80%</div>
              </div>
            )}
            {this.state.shouldColorChanged && (
              <div className="map-legend">
                <div>{this.props.t("reliability")}</div>
                <div className="good-confid-cb back">&gt; 95%</div>
                <div className="med-high-confid-cb back my-1">90% - 95%</div>
                <div className="med-confid-cb back my-1">85% - 90%</div>
                <div className="med-low-confid-cb back my-1">80% - 85%</div>
                <div className="bad-confid-cb back">&le; 80%</div>
              </div>
            )}
            <Map
              center={position}
              zoom={6}
              zoomControl={false}
              attributionControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> contributors'
                url={`https://api.maptiler.com/maps/positron/256/{z}/{x}/{y}.png?key=${mapTilerKey}&language=fr`}
                maxZoom={14}
                minZoom={5}
              />
              <AttributionControl prefix={Constants.MAP_ATTRIBUTIONS_HTML} />
              <ZoomControl position="topright" />
              {this.state.selectedMode &&
                this.state.sectionsPerMode[this.state.selectedMode] && this.state.selectedYear &&
                this.state.sectionsPerMode[
                  this.state.selectedMode
                ].sections[
                  this.state.selectedYear.value
                ][this.state.selectedYear.label].map((sect) =>
                  sect.dlat !== sect.alat &&
                  sect.dlon !== sect.alon &&
                  sect.dlat != null &&
                  sect.dlon != null &&
                  sect.alat != null &&
                  sect.alon != null ? (
                    <CurvePath
                      className={
                        "leaflet-train-arrow " +
                        getClassFromConfidValue(sect[this.state.selectedDelay], this.state.shouldColorChanged)
                      }
                      curvePath={this.getCurveWithArrow(
                        [sect.dlat, sect.dlon],
                        [sect.alat, sect.alon]
                      )}
                      key={sect.dname + sect.aname}
                    >
                      <Tooltip interactive={false} sticky={true}>
                        <strong>{sect[this.state.selectedDelay]}%</strong>{" "}
                        {sect.dname} &rarr; {sect.aname}
                      </Tooltip>
                    </CurvePath>
                  ) : undefined
                )}
            </Map>
          </div>
        </div>
      </>
    );
  }
}

export default withTranslation()(MapPage);
